/**
 * MockupTemplatePicker — sélection multi-mockup avant publication Etsy
 * (S6 PR2-D bis — multi-mockup checkbox).
 *
 * Affiche **tous** les templates compatibles format pour l'œuvre via
 * `useBrowseForWork({work_id, format_slug})`, triés par qualité décroissante
 * (composition + palette + rotation + freshness). Les top 5 par qualité sont
 * auto-pré-sélectionnés au render initial. L'utilisatrice peut décocher /
 * cocher dans la limite [1, 8] (max Etsy hors œuvre nue + deliverable
 * preview). Contrairement à `useMatchForWork` (séquence Etsy contrainte
 * ~10 items max), le browse mode ne cape pas et ne filtre pas par usage
 * récent — Stéphanie voit tout le catalogue compatible et choisit elle-même.
 *
 * UX :
 *   - Modal centré (large) avec deux zones :
 *     - Haut : bande persistante "Sélectionnés (X/8)" — vignettes
 *       horizontales des mockups choisis, × pour retirer, mini toggle
 *       Pinterest par vignette.
 *     - Bas (scrollable) : recherche + chips kind + liste des templates
 *       *restants* (les sélectionnés "remontent" dans la bande haute).
 *   - Bouton "Publier" disabled si 0 ou >8 sélection.
 *
 * Verrous tenus :
 *   - `<Button>` pour les actions (pas de `<button>` natif)
 *   - Aucun hex couleur en dur (tokens DS via CSS layer)
 */

import { useMemo, useState } from 'react'
import { Loader2, X } from 'lucide-react'

import Button from '@/components/ui/Button'
import Checkbox from '@/components/ui/Checkbox'
import ChipMultiSelect from '@/components/ui/ChipMultiSelect'
import Modal from '@/components/ui/Modal'
import SearchInput from '@/components/ui/SearchInput'
import { useBrowseForWork } from '@/hooks/useMockupTemplates'
import {
  FRAME_STYLE_FR,
  MOCKUP_ROLE_FR,
  MOCKUP_TEMPLATE_KIND_FR,
  MOCKUP_TEMPLATE_KIND_OPTIONS,
  ROOM_TYPE_FR,
} from '@/types/mockup-templates'
import type { MockupTemplateKind } from '@/types/mockup-templates'

import './MockupTemplatePicker.css'

const MAX_MOCKUPS_PER_LISTING = 8
const TOP_PRE_SELECTED_COUNT = 5

interface MockupTemplatePickerProps {
  /** Œuvre cible — alimente `useBrowseForWork`. */
  workId: number
  /** Format slug de la variante cible (ex: "30x40") — filtre les templates
   *  dont le ratio masque correspond. Optionnel : si null, tous ratios. */
  formatSlug?: string | null
  /** Titre œuvre, affiché dans le kicker du Modal. */
  workTitle: string
  isOpen: boolean
  onClose: () => void
  /** Confirme la sélection avec les templates choisis (1 à 8) et les
   *  templates marqués pour Pinterest (S7-PR2 T7 — sous-ensemble strict
   *  de `mockupTemplateIds`, intersection ordonnée selon le matching).
   *  Le caller enchaîne avec l'enqueue (publish initial OU update PR2-C).
   *
   *  `pinterestTemplateIds` peut être vide même si `mockupTemplateIds`
   *  est non-vide — cas où Stéphanie veut publier sur Etsy seulement.
   *  L'inverse (Pinterest non vide, Etsy vide) est impossible par
   *  construction UI (le toggle Pinterest n'apparaît que sur les
   *  mockups Etsy cochés). */
  onConfirm: (mockupTemplateIds: number[], pinterestTemplateIds: number[]) => void
  /** Indique que l'action est en cours côté caller — désactive le bouton
   *  pour éviter les double-clics. */
  isSubmitting?: boolean
  /** S6 PR2-C — IDs des templates DÉJÀ attachés à la publication active.
   *  Si fourni :
   *    - default selection = ces IDs (au lieu du top 5 du matching)
   *    - badge "Actuel" sur les items dont l'id ∈ currentMockupTemplateIds
   *  `null` ou `undefined` = mode publish initial (top 5 pré-sélectionnés). */
  currentMockupTemplateIds?: number[] | null
  /** Libellé du bouton de confirmation. Défaut : "Publier sur Etsy".
   *  Pour update PR2-C : "Mettre à jour les mockups". */
  confirmLabel?: string
  /** Libellé verbe en cours (pendant `isSubmitting`). Défaut : "Publication…". */
  submittingLabel?: string
  /** Kicker affiché en haut du Modal. Défaut : "Publication Etsy". */
  kicker?: string
  /** Titre principal du Modal. Défaut : "Choisir des mockups pour « X »". */
  title?: string
}

export default function MockupTemplatePicker({
  workId,
  formatSlug,
  workTitle,
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
  currentMockupTemplateIds,
  confirmLabel = 'Publier sur Etsy',
  submittingLabel = 'Publication…',
  kicker = 'Publication Etsy',
  title,
}: MockupTemplatePickerProps) {
  // Pattern "default + user override" pour éviter `setState` dans effect
  // (verrou react-hooks/set-state-in-effect). `userSelectedIds === null`
  // ⇒ l'utilisatrice n'a pas encore touché → on retourne le default
  // dérivé (top 5 ou liste actuelle selon mode). Au premier toggle,
  // `userSelectedIds` est initialisé à partir du default puis mute.
  // Reset à la fermeture du modal.
  const [userSelectedIds, setUserSelectedIds] = useState<Set<number> | null>(null)

  // S7-PR2 T7 — sélection Pinterest indépendante. `null` = pas encore
  // touchée, on dérive le default (= mirror de la sélection Etsy : si
  // Etsy coché → Pinterest coché par défaut, Stéphanie décoche au cas
  // par cas). Au 1er toggle on initialise depuis le default.
  const [userPinterestSelectedIds, setUserPinterestSelectedIds] = useState<Set<number> | null>(null)

  // Filtres intra-picker — depuis le passage en browse mode (tout le
  // catalogue compatible, 200-500 items), Stéphanie a besoin d'un moyen
  // de réduire le pool rapidement. Recherche par nom + filtre par kind.
  // Reset à la fermeture du modal (mêmes setters que la sélection).
  const [searchQuery, setSearchQuery] = useState('')
  const [filterKinds, setFilterKinds] = useState<MockupTemplateKind[]>([])

  const matchQuery = useBrowseForWork(
    isOpen
      ? {
          work_id: workId,
          format_slug: formatSlug ?? undefined,
        }
      : null,
  )
  const rawMatches = useMemo(() => matchQuery.data ?? [], [matchQuery.data])

  // Set des templates "actuellement attachés" — pour badge "Actuel" et
  // pour calculer le default en mode update PR2-C.
  const currentSet = useMemo(
    () => new Set(currentMockupTemplateIds ?? []),
    [currentMockupTemplateIds],
  )

  // Mode update : on déplace les templates actuellement attachés en QUEUE
  // de la liste (au lieu de les laisser au top par stabilité de score).
  // Raison : Stéphanie sait déjà ce qu'elle a choisi ; ce qu'elle veut voir
  // au premier coup d'œil ce sont les options qu'elle n'a pas encore prises.
  // En mode publish initial (currentSet vide), pas de réordonnancement —
  // l'ordre serveur (qui encode déjà la grammaire opening/rôle) est respecté.
  const matches = useMemo(() => {
    if (currentSet.size === 0) return rawMatches
    const newOnes = rawMatches.filter((m) => !currentSet.has(m.template.id))
    const currentOnes = rawMatches.filter((m) => currentSet.has(m.template.id))
    return [...newOnes, ...currentOnes]
  }, [rawMatches, currentSet])

  // Top N par qualité — dérivé du tri serveur (rawMatches en ordre quality
  // décroissant), AVANT le réordonnancement update mode et AVANT le filtrage
  // user. Permet d'afficher un badge "Top X" stable sur les meilleurs
  // candidats même quand ils descendent dans la liste affichée (mode update
  // + filtre kind/search).
  const topRankById = useMemo(() => {
    const ranks = new Map<number, number>()
    rawMatches.slice(0, TOP_PRE_SELECTED_COUNT).forEach((match, i) => {
      ranks.set(match.template.id, i + 1)
    })
    return ranks
  }, [rawMatches])

  // Default selection :
  //   - Mode update (currentMockupTemplateIds fourni & non vide) → la
  //     liste actuelle. L'utilisatrice voit son état réel et coche/
  //     décoche pour refléter le diff souhaité.
  //   - Mode publish initial (currentMockupTemplateIds null/undefined)
  //     → top 5 du matching (UX existante).
  const defaultSelectedIds = useMemo(() => {
    if (currentMockupTemplateIds && currentMockupTemplateIds.length > 0) {
      return new Set(currentMockupTemplateIds)
    }
    return new Set(matches.slice(0, TOP_PRE_SELECTED_COUNT).map((m) => m.template.id))
  }, [currentMockupTemplateIds, matches])

  const selectedIds = userSelectedIds ?? defaultSelectedIds

  // Default Pinterest = mirror Etsy (chaque Etsy coché est aussi
  // Pinterest par défaut — Stéphanie décoche au cas par cas si un
  // mockup ne se croppe pas bien en 2:3). Recalcule à chaque change
  // d'Etsy sélection tant que l'utilisatrice n'a pas touché Pinterest
  // (userPinterestSelectedIds=null).
  const pinterestSelectedIds = useMemo(() => {
    if (userPinterestSelectedIds === null) {
      return new Set(selectedIds)
    }
    // Garde-fou : Pinterest ne peut JAMAIS être coché sans Etsy coché.
    // On filtre côté lecture pour le cas où l'utilisatrice aurait décoché
    // Etsy après avoir touché Pinterest.
    return new Set([...userPinterestSelectedIds].filter((id) => selectedIds.has(id)))
  }, [userPinterestSelectedIds, selectedIds])

  const toggleId = (id: number) => {
    setUserSelectedIds(() => {
      // Init `userSelectedIds` à partir du default au 1er toggle.
      const base = userSelectedIds ?? defaultSelectedIds
      const next = new Set(base)
      if (next.has(id)) {
        next.delete(id)
      } else if (next.size < MAX_MOCKUPS_PER_LISTING) {
        next.add(id)
      }
      // Si déjà au cap : on ne fait rien (le check disabled côté UI suffit
      // mais defense-in-depth ici).
      return next
    })
  }

  const togglePinterest = (id: number) => {
    setUserPinterestSelectedIds(() => {
      // Init à partir du default (= mirror Etsy) au 1er toggle.
      const base = userPinterestSelectedIds ?? new Set(selectedIds)
      const next = new Set(base)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // Liste affichée en bande haute (sélectionnés), dans l'ordre serveur.
  // On itère sur `matches` (déjà réordonné en mode update) et NON sur
  // `filteredMatches` — la sélection peut contenir des ids masqués par
  // un filtre user actif.
  const selectedMatches = useMemo(
    () => matches.filter((m) => selectedIds.has(m.template.id)),
    [matches, selectedIds],
  )

  // Filtrage user (recherche + chips kind) — appliqué sur `matches` puis
  // exclusion des sélectionnés (ils vivent dans la bande haute, pas dans
  // la liste basse). Ne touche pas l'ordre relatif des non-sélectionnés.
  const filteredMatches = useMemo(() => {
    let next = matches.filter((m) => !selectedIds.has(m.template.id))
    if (filterKinds.length > 0) {
      const kindSet = new Set<string>(filterKinds)
      next = next.filter((m) => kindSet.has(String(m.template.kind)))
    }
    const q = searchQuery.trim().toLowerCase()
    if (q) {
      next = next.filter((m) => m.template.name.toLowerCase().includes(q))
    }
    return next
  }, [matches, selectedIds, filterKinds, searchQuery])

  const totalNonSelected = useMemo(
    () => matches.filter((m) => !selectedIds.has(m.template.id)).length,
    [matches, selectedIds],
  )

  const handleConfirm = () => {
    if (selectedIds.size === 0 || selectedIds.size > MAX_MOCKUPS_PER_LISTING) return
    // Préserve l'ordre serveur (quality DESC) → l'API reçoit les ids dans cet
    // ordre, qui devient le rang Etsy 1..N.
    const orderedIds = matches.map((m) => m.template.id).filter((id) => selectedIds.has(id))
    const pinterestOrderedIds = orderedIds.filter((id) => pinterestSelectedIds.has(id))
    onConfirm(orderedIds, pinterestOrderedIds)
  }

  const handleClose = () => {
    setUserSelectedIds(null)
    setUserPinterestSelectedIds(null)
    setSearchQuery('')
    setFilterKinds([])
    onClose()
  }

  const canConfirm = selectedIds.size > 0 && selectedIds.size <= MAX_MOCKUPS_PER_LISTING
  const atCap = selectedIds.size >= MAX_MOCKUPS_PER_LISTING

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      kicker={kicker}
      title={title ?? `Choisir des mockups pour « ${workTitle} »`}
      size="large"
      className="mockup-template-picker-modal"
    >
      <div className="mockup-template-picker-selected-strip" aria-label="Mockups sélectionnés">
        <div className="mockup-template-picker-selected-header">
          <strong className="ui-body-sm">Sélectionnés</strong>
          <span
            className={
              atCap
                ? 'mockup-template-picker-selected-count mockup-template-picker-selected-count--full'
                : 'mockup-template-picker-selected-count'
            }
          >
            {selectedIds.size}/{MAX_MOCKUPS_PER_LISTING}
          </span>
        </div>
        {selectedMatches.length === 0 ? (
          <p className="ui-body-sm ui-text-muted mockup-template-picker-selected-empty">
            Aucun mockup sélectionné. Coche dans la liste ci-dessous.
          </p>
        ) : (
          <ul className="mockup-template-picker-selected-list" aria-label="Mockups choisis">
            {selectedMatches.map((match) => {
              const template = match.template
              const isPinterestSelected = pinterestSelectedIds.has(template.id)
              return (
                <li key={template.id} className="mockup-template-picker-selected-item">
                  <Button
                    type="button"
                    variant="quiet"
                    onClick={() => toggleId(template.id)}
                    className="mockup-template-picker-selected-thumb-wrap"
                    aria-label={`Retirer ${template.name}`}
                    title={`Retirer ${template.name}`}
                  >
                    {template.image_url ? (
                      <img
                        src={template.image_url}
                        alt=""
                        className="mockup-template-picker-selected-thumb"
                        loading="lazy"
                      />
                    ) : (
                      <div className="mockup-template-picker-selected-thumb-placeholder" />
                    )}
                    <span className="mockup-template-picker-selected-remove" aria-hidden="true">
                      <X size={14} strokeWidth={2.5} />
                    </span>
                  </Button>
                  <label className="mockup-template-picker-selected-pinterest">
                    <Checkbox
                      checked={isPinterestSelected}
                      onChange={() => togglePinterest(template.id)}
                      aria-label={`Aussi pour Pinterest : ${template.name}`}
                    />
                    <span className="ui-body-sm">Pinterest</span>
                  </label>
                </li>
              )
            })}
          </ul>
        )}
      </div>
      <Modal.Body>
        {matchQuery.isLoading ? (
          <p className="ui-body ui-text-muted" role="status">
            Recherche des mockups pertinents…
          </p>
        ) : matchQuery.isError ? (
          <p className="ui-body" role="alert">
            Impossible de charger les recommandations de templates.
          </p>
        ) : matches.length === 0 ? (
          <div>
            <p className="ui-body">
              Aucun template ne correspond à cette œuvre pour le format demandé.
            </p>
            <p className="ui-body ui-text-muted">
              Tu peux ajouter des templates depuis <em>Réglages → Mockups</em>, ou re-essayer sans
              filtre de format.
            </p>
          </div>
        ) : (
          <>
            <div className="mockup-template-picker-filters">
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Rechercher un template par nom…"
                ariaLabel="Rechercher un template"
                className="mockup-template-picker-search"
              />
              <ChipMultiSelect
                options={MOCKUP_TEMPLATE_KIND_OPTIONS.map((k) => ({
                  value: k,
                  label: MOCKUP_TEMPLATE_KIND_FR[k],
                }))}
                value={filterKinds}
                onChange={setFilterKinds}
                ariaLabel="Filtrer par type de mockup"
              />
              <p className="mockup-template-picker-stats ui-body-sm ui-text-muted">
                {filteredMatches.length === totalNonSelected
                  ? `${totalNonSelected} template${totalNonSelected > 1 ? 's' : ''} restant${
                      totalNonSelected > 1 ? 's' : ''
                    }`
                  : `${filteredMatches.length} sur ${totalNonSelected} templates restants`}
              </p>
            </div>
            {filteredMatches.length === 0 ? (
              <p className="ui-body ui-text-muted" role="status">
                {totalNonSelected === 0
                  ? 'Tous les templates compatibles sont déjà sélectionnés.'
                  : 'Aucun template ne correspond aux filtres. Essaie de retirer un filtre ou de modifier ta recherche.'}
              </p>
            ) : (
              <ul className="mockup-template-picker-grid" aria-label="Templates">
                {filteredMatches.map((match) => {
                  const template = match.template
                  const isCurrentlyAttached = currentSet.has(template.id)
                  const topRank = topRankById.get(template.id)
                  const kindLabel = MOCKUP_TEMPLATE_KIND_FR[template.kind]
                  const roomLabel = template.room_type ? ROOM_TYPE_FR[template.room_type] : null
                  const frameLabel = template.frame_style
                    ? FRAME_STYLE_FR[template.frame_style]
                    : null
                  const roleLabel = MOCKUP_ROLE_FR[match.role]
                  const metaLine = [kindLabel, roomLabel, frameLabel].filter(Boolean).join(' · ')
                  return (
                    <li key={template.id} className="mockup-template-picker-grid-item-wrap">
                      <Button
                        type="button"
                        variant="quiet"
                        role="checkbox"
                        aria-checked={false}
                        aria-label={template.name}
                        disabled={atCap}
                        onClick={() => toggleId(template.id)}
                        className="mockup-template-picker-grid-item"
                        title={template.name}
                      >
                        {template.image_url ? (
                          <img
                            src={template.image_url}
                            alt=""
                            className="mockup-template-picker-grid-thumb"
                            loading="lazy"
                          />
                        ) : (
                          <div className="mockup-template-picker-grid-thumb-placeholder" />
                        )}
                        {topRank !== undefined ? (
                          <span className="mockup-template-picker-badge mockup-template-picker-badge--top mockup-template-picker-grid-badge">
                            Top {topRank}
                          </span>
                        ) : isCurrentlyAttached ? (
                          <span className="mockup-template-picker-badge mockup-template-picker-badge--current mockup-template-picker-grid-badge">
                            Actuel
                          </span>
                        ) : null}
                        <div className="mockup-template-picker-grid-overlay" aria-hidden="true">
                          <strong className="mockup-template-picker-grid-name">
                            {template.name}
                          </strong>
                          <span className="mockup-template-picker-grid-role ui-body-sm">
                            {roleLabel}
                          </span>
                          {metaLine ? (
                            <span className="mockup-template-picker-grid-meta ui-body-sm">
                              {metaLine}
                            </span>
                          ) : null}
                        </div>
                      </Button>
                    </li>
                  )
                })}
              </ul>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer
        leftActions={
          <span className="mockup-template-picker-counter ui-body-sm">
            {selectedIds.size}/{MAX_MOCKUPS_PER_LISTING} sélectionnés
          </span>
        }
      >
        <Button type="button" variant="neutral" onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleConfirm}
          disabled={!canConfirm || isSubmitting}
        >
          {isSubmitting ? (
            <Loader2 aria-hidden="true" size={16} className="mockup-template-picker-spinner" />
          ) : null}
          {isSubmitting ? submittingLabel : confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
